import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../Components/Buttons";
import {
  FormImage,
  FormInput,
  FormSection,
  FormSelect,
  FormSwitch,
  Toggle,
} from "../../../Components/Form";
import { formatDateOnly, formatDateTimeLocal, formattedDate, ToastMessage, toOptions, useDebouncedCallback } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { user_profile_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import { useFetchAllDistrictsQuery } from "../../../stores/api/district.api";
import { Loader2, SearchIcon } from "lucide-react";
import { useFetchPatientByTrackingMutation, useFetchPhysiciansAllQuery } from "../../../stores/api/patient.api";
import { PatientSearchHelper } from "../../../stores/api/helpers/patients.helper";
import { patientDetailsSchema } from "./Components/Schemas";
import { PatientProfile } from "../../../Components/PatientProfile";
import patientVisitsHelper from "../../../stores/api/helpers/patient.visit.helper";
import { useCreatePatientVisitMutation, useUpdatePatientVisitMutation } from "../../../stores/api/patient.visit.api";
import { useFetchAllDoctorsQuery } from "../../../stores/api/users.api";

const CreatePatientVisit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patientVisitDetail = location?.state?.patient_visit;

  const [createPatientVisit, { isLoading: creating }] = useCreatePatientVisitMutation();
  const [updatePatientVisit, { isLoading: updating }] = useUpdatePatientVisitMutation();
  const [searchPatient] = useFetchPatientByTrackingMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    patient_id: patientVisitDetail?.patient_id || "",
    visit_date: patientVisitDetail?.visit_date || "",
    attendant_id: patientVisitDetail?.attendant_id || "",
    diagnosis: patientVisitDetail?.diagnosis || "",
    prescription: patientVisitDetail?.prescription || "",
    next_visit_plan: patientVisitDetail?.next_visit_plan || "",
    remarks: patientVisitDetail?.remarks || "",
    is_referred: patientVisitDetail?.is_referred || false,
    hospital_name: patientVisitDetail?.patient_referral?.hospital_name || "",
    referred_location: patientVisitDetail?.patient_referral?.referred_location ?? "",
    first_aid_given: patientVisitDetail?.patient_referral?.first_aid_given || "",
    cause_of_referral: patientVisitDetail?.patient_referral?.cause_of_referral || "",
    status: patientVisitDetail?.status ? true : false,
  });
  const [searching, setSearching] = useState(false);
  const [patientDetails, setPatientDetails] = useState(patientDetailsSchema)
  const [unitId, setUnitId] = useState(
    formData.unit_id ?? patientDetails.unit_id
  )
  const isReferred =
  patientVisitDetail?.is_referred === true ||
  patientVisitDetail?.is_referred === "true" ||
  patientVisitDetail?.is_referred === 1;

  const [checked, setChecked] = useState(isReferred);
 
  const physicians = [];

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }
  useEffect(() => {
    if (patientVisitDetail) {
      const tracking_number = patientVisitDetail?.patient?.system_number || patientVisitDetail?.patient?.patient_cnic || patientVisitDetail?.patient?.prescription_pad_no;
      setField("tracking_number", tracking_number)
      handlePatientSearch(tracking_number);
    }
  }, [])
  const handleToggle = (val) => {
    setChecked(val)
    if (!val) {
    setFormData((prev) => ({
      ...prev,
      hospital_name: "",
      referred_location: "",
      first_aid_given: "",
      cause_of_referral: "",
    }));
  }
    setFormData((prev) => ({ ...prev, is_referred: val }))
  }
  const handlePatientSearch = async (tracking_number = null) => {
    setSearching(true);
    try {
      const result = await new Promise((resolve) => {
        setTimeout(() => {
          resolve(
            searchPatient(
              PatientSearchHelper.forCreate({
                tracking_number: formData.tracking_number || tracking_number || "",
              })
            ).unwrap()
          );
        }, 300);
      });
      const patient = result?.patient;
      setField("patient_id", patient?.id)
      setPatientDetails({
        patient_name: patient?.patient_name || "",
        father_husband_name: patient?.father_husband_name || "",
        patient_type: patient?.patient_type || "",
        gender: patient?.gender || "",
        system_number: patient?.system_number || "",
        dob: patient?.dob || "",
        age: patient?.age || "",
        mobile: patient?.mobile || "",
        patient_cnic: patient?.patient_cnic || "",
        unit_name: patient?.unit?.name || "",
        district_name: patient?.unit?.district?.name || "",
      });
    } catch (error) {
      handleError(error, "Something went wrong.");
    } finally {
      setSearching(false);
    }
  };
  const resetForm = () =>
    setFormData({
      patient_id: "",
      visit_date: "",
      attendant_id: "",
      diagnosis: "",
      prescription: "",
      remarks:"",
      next_visit_plan: "",
      hospital_name: "",
      referred_location: "",
      first_aid_given: "",
      status: "",
      cause_of_referral: "",
    });
    const { data: allPhysiciansRow = [] } =
      useFetchAllDoctorsQuery();
    const allPhysicians = useMemo(
      () =>
        (allPhysiciansRow || []).map((r) => ({
          label: r.name,
          value: r.id,
        })),
      [allPhysiciansRow],
    );
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (patientVisitDetail) {
        console.log(typeof formData.status);
        await updatePatientVisit(
          patientVisitsHelper.forUpdate(patientVisitDetail.id, formData),
        ).unwrap();
      } else {
        console.log(formData)
        await createPatientVisit(patientVisitsHelper.forCreate(formData)).unwrap();
      }
      ToastMessage({
        type: "success",
        title: "Operation completed successfully.",
        message: patientVisitDetail
          ? "Patient Visit updated successfully!"
          : "Patient Visit created successfully!",
      });
      if (!patientVisitDetail) resetForm();
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };
  
  const patientStatus = formData?.status || "OPD";

  return (
    <div className="p-4">
      <BreadCrumb title={patientVisitDetail ? "Edit Patient Visit" : "Create Patient Visit"} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <form className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
            <FormSection title=" Patient Visit Form">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <FormSection className="pt-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Patient <span className="text-red-500">*</span>
                    </label>

                    <div className="flex overflow-hidden rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-green-500">
                      <input
                        type="text"
                        placeholder="System Number, Prescription Pad No, or CNIC"
                        value={formData.tracking_number}
                        onChange={(e) => setField("tracking_number", e.target.value)}
                        className="flex-1 px-3 py-[8px] outline-none border-none"
                      />
                      <button
                        type="button"
                        onClick={handlePatientSearch}
                        disabled={searching}
                        className="px-5 bg-green-600 text-white hover:bg-green-700 transition"
                      >
                        {searching ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <SearchIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    <p className="mt-2 text-xs text-gray-500">
                      Search using the patient's System Number, Prescription Pad Number, or CNIC.
                    </p>
                  </div>
                </FormSection>

                <FormSection title="" className="pt-3">
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Visit Date"
                      isRequired
                      type="datetime-local"
                      value={formatDateTimeLocal(formData?.visit_date)}
                      onChange={(val) => setField("visit_date", val)}
                      placeholder="Hospital Name"
                    />

                    <FormSelect
                      label="Attendant"
                      isRequired
                      options={allPhysicians}
                      value={formData?.attendant_id}
                      onChange={(val) => setField("attendant_id", val ?? "")}
                      blurInputOnSelect
                    />
                  </div>
                </FormSection>
                <FormSection title="" className="pt-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Assessment/Diagnosis"
                      isRequired
                      type="textarea"
                      value={formData?.diagnosis}
                      onChange={(val) => setField("diagnosis", val)}
                      placeholder="Write Details about Assessment or Diagnosis..."
                      rows={4}
                    />

                    <FormInput
                      label="Prescription"
                      isRequired
                      type="textarea"
                      value={formData?.prescription}
                      onChange={(val) => setField("prescription", val)}
                      placeholder="Write Prescription..."
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <FormInput
                      label="Next Planned Visit"
                      isRequired
                      type="datetime-local"
                      value={formatDateTimeLocal(formData?.next_visit_plan)}
                      onChange={(val) => setField("next_visit_plan", val)}
                      placeholder="Hospital Name"
                    />
                  </div>
                  <div className="mt-2">
                    <FormInput
                      label="Investigation / Remarks / Tests"
                      isRequired
                      type="textarea"
                      value={formData?.remarks}
                      onChange={(val) => setField("remarks", val)}
                      placeholder="Write Investigation / Remarks / Tests..."
                      rows={4}
                    />
                  </div>
                </FormSection>

                <FormSection
                  title="Patient Referral Details"
                  className="pt-3"
                >
                  <div className="flex items-center  gap-3 mt-3">
                    <label htmlFor="">Referr Patient ?</label>
                    <Toggle
                      checked={checked}
                      onChange={handleToggle}
                    />
                  </div>
                  {checked && <><div className="grid grid-cols-2 gap-4">
                    <FormInput
                      label="Hospital Name"
                      isRequired
                      type="text"
                      value={formData?.hospital_name}
                      onChange={(val) => setField("hospital_name", val)}
                      placeholder="Hospital Name"
                    />
                    <FormInput
                      label="Referred Location "
                      isRequired
                      type="text"
                      value={formData?.referred_location}
                      onChange={(val) => setField("referred_location", val)}
                      placeholder="Referred Location "
                    />

                    <FormInput
                      label="First Aid Given "
                      isRequired
                      type="textarea"
                      value={formData?.first_aid_given}
                      onChange={(val) => setField("first_aid_given", val)}
                      placeholder="Write first aid details..."
                      rows={4}
                    />

                    <FormInput
                      label="Cause of Referral"
                      isRequired
                      type="textarea"
                      value={formData?.cause_of_referral}
                      onChange={(val) => setField("cause_of_referral", val)}
                      placeholder="Write reason for referral..."
                      rows={4}
                    />
                  </div></>}
                </FormSection>
              </div>
            </FormSection>
            <FormSection title="Status">
              <div className="flex gap-3 items-center">
                <FormSwitch checked={formData.status} onChange={(val) => setField("status", Boolean(val))} />
              </div>
            </FormSection>
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  navigate(ROUTE_PATHS.PATIENT_VISIT_LIST);
                }}
                className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="!bg-green-500 !text-white hover:!bg-green-600"
                loading={loading}
                disabled={loading}
                onClick={onSubmit}
                loadingText={patientVisitDetail ? "Updating..." : "Creating..."}
              >
                {patientVisitDetail ? "Update Changes" : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>

        <div className="xl:col-span-1">
          <PatientProfile patientDetails={patientDetails} />
        </div>
      </div>
    </div>
  );
};

export default CreatePatientVisit;
